import { Button } from '@design-system/components/button/button';
import { Tag } from '@design-system/components/tag/tag';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import { EmptyStateGuideInfo } from './emptyStateGuideInfo';
import { SubscriptionEmptyStateOptionsByMode } from '../mappers';

export type SubscriptionEmptyStateProps = {
  name: string;
  guideLink: string;
  options: SubscriptionEmptyStateOptionsByMode;
};

export const SubscriptionEmptyState = ({
  name,
  guideLink,
  options,
}: SubscriptionEmptyStateProps) => (
  <div className="w-full h-full flex flex-col gap-8 items-center">
    <EmptyStateGuideInfo
      name={name}
      type="Subscription"
      guideLink={guideLink}
    />
    <Typography
      variant="body"
      size="large"
      className="max-w-[694px] text-center"
    >
      Create your first collection of workouts for your subscription business.
      There are two ways to create a subscription offering on our app:
    </Typography>

    <div className="flex flex-col md:flex-row gap-6">
      {options.map((option) => (
        <div
          key={option.title}
          className="p-4 w-full h-full md:w-[390px] bg-white rounded-[10px] flex flex-col gap-4"
        >
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              <Typography variant="headline" size="small">
                {option.title}
              </Typography>
              {option.tag && (
                <Tag
                  label={option.tag.label}
                  readonly={option.tag.readonly}
                  className={option.tag.className}
                />
              )}
            </div>
            <Typography variant="body" size="large">
              {option.details}
            </Typography>
          </div>
          <div className="flex flex-col gap-4 flex-grow">
            {option.list.map((item) => (
              <div key={item.details} className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={item.icon} className="w-[20px]" />
                  <Typography variant="body" size="large">
                    {item.title}
                  </Typography>
                </div>
                <Typography
                  variant="body"
                  size="medium"
                  className={clsx('pl-7', sprinkles({ color: 'N-80' }))}
                >
                  {item.details}
                </Typography>
              </div>
            ))}
          </div>
          <div className="flex gap-4 items-center">
            {option.cta.map((cta) =>
              cta?.to ? (
                <a
                  key={cta.label}
                  href={cta.to}
                  className={sprinkles({
                    color: 'P-80',
                    fontFamily: 'visuelt',
                    px: 'xl',
                  })}
                  target="_blank"
                  rel="noreferrer"
                >
                  {cta.label}
                </a>
              ) : (
                <Button
                  testId={cta.testId}
                  key={cta.label}
                  variant={cta.variant}
                  color={cta.color}
                  className="w-[135px] text-[14px]"
                  onClick={cta.onClick}
                  disabled={cta.disabled}
                >
                  {cta.label}
                </Button>
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);
